import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { apiGet, apiPost, apiPut } from '@/api/axios-client'
import { getErrorMessage } from '@/api/errors'
import { cardsApi } from '@/features/cards/api'
import type { Card } from '@/types/entities'

export interface DiaryOutcome {
  id: string
  text: string
  cardId: string | null
  minutes: number
  done: boolean
  card?: { id: string; code: string | null; title: string; status: string } | null
}
export interface DiaryReview {
  reviewedAt: string
  happiness: number | null
  moved: string
  stuck: string
  tomorrow: string
  notes: string
}
export interface HoursPlan {
  availableMinutes: number
  allocatedMinutes: number
  bufferMinutes: number
  overBy: number
}
export interface DiaryEntry {
  date: string
  off: boolean
  outcomes: DiaryOutcome[]
  attention: string
  delegate: Array<{ text: string; person: string }>
  availableMinutes: number
  filledAt: string
  review: DiaryReview | null
  plan: HoursPlan
}
export interface Gate {
  today: string
  entry: DiaryEntry | null
  toReview: (DiaryEntry & { loggedMinutes: number }) | null
}
export interface DiaryMonth {
  month: string
  people: Array<{ userId: string; displayName: string }>
  entries: Array<DiaryEntry & { userId: string; loggedMinutes: number }>
  logged: Record<string, Record<string, number>>
}
export interface PlanInput {
  off?: boolean
  availableMinutes: number
  outcomes: Array<{ id?: string; text: string; cardId: string | null; minutes: number; done?: boolean }>
  attention: string
  delegate: Array<{ text: string; person: string }>
}
export interface ReviewInput {
  date: string
  happiness: number | null
  moved: string
  stuck: string
  tomorrow: string
  notes: string
  outcomeDone: Record<string, boolean>
}

const tz = () => new Date().getTimezoneOffset()
export const diaryKeys = {
  gate: ['diary', 'gate'] as const,
  month: (month: string) => ['diary', 'month', month] as const,
}

export function useGate(enabled: boolean) {
  return useQuery({
    queryKey: diaryKeys.gate,
    queryFn: () => apiGet<Gate>('diary/gate', { tz: tz() }),
    enabled,
    staleTime: 60_000,
    retry: false,
  })
}

export function useDiaryMonth(month: string) {
  return useQuery({
    queryKey: diaryKeys.month(month),
    queryFn: () => apiGet<DiaryMonth>('diary', { month, tz: tz() }),
    staleTime: 30_000,
  })
}

export function useSavePlan() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: PlanInput) => apiPut<{ entry: DiaryEntry }>('diary/today', { tz: tz(), ...input }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['diary'] })
      void queryClient.invalidateQueries({ queryKey: ['planner'] })
      void queryClient.invalidateQueries({ queryKey: ['worktime'] })
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}

export function useSaveReview() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: ReviewInput) => apiPut<{ entry: DiaryEntry }>('diary/review', { tz: tz(), ...input }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['diary'] }),
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}

/** The caller's own open work, most pressing first, for linking an outcome to an item. */
export function useMyOpenWork(workspaceId: string, userId: string, enabled: boolean): Card[] {
  const query = useQuery({
    queryKey: ['diary', 'my-open-work', workspaceId, userId],
    queryFn: () =>
      cardsApi.query({
        filter: { workspaceId, assigneeIds: [userId], status: ['todo', 'in_progress', 'in_review'], archived: false },
        limit: 60,
        cursor: null,
      }),
    enabled: enabled && Boolean(workspaceId && userId),
    staleTime: 60_000,
  })
  return useMemo(
    () => [...(query.data?.items ?? [])].sort((a, b) => (a.dueAt ? Date.parse(a.dueAt) : Infinity) - (b.dueAt ? Date.parse(b.dueAt) : Infinity)),
    [query.data],
  )
}

export interface CommitmentCheck {
  text: string
  specific: boolean
  issue: string
  example: string
}

/** Asks whether commitments are specific and measurable. Advice only; a failure never stops anyone planning. */
export function useCheckCommitments() {
  return useMutation({
    mutationFn: (texts: string[]) => apiPost<{ items: CommitmentCheck[]; allSpecific: boolean }>('diary/check', { texts }),
  })
}
